import { fetchWithTrace } from '../fetchWithTrace';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/** POST helper that always sends/receives JSON */
async function postJSON(path, body) {
  const { data } = await fetchWithTrace(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: body ?? {},
  });
  return data;
}

/** GET helper that always expects JSON */
async function getJSON(path) {
  const { data } = await fetchWithTrace(`${API_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
  });
  return data;
}

/**
 * Existing: Generate full screenplay (kept as-is).
 * NOTE: This call returns whatever the backend currently returns (historically { text }).
 * We do not change its contract to avoid breaking callers.
 */
export async function generateScreenplay(title, model) {
  const { text } = await postJSON('/api/text/generateScreenplay', { title, model });
  return text;
}

/**
 * NEW: Generate screenplay OUTLINE quickly (strict JSON).
 * Returns: {
 *   status: "ok",
 *   request_id: string,
 *   elapsed_ms: number,
 *   screenplayOutline: { title: string, scenes: [{ number, title, synopsis, characters: string[] }] },
 *   detailJobId?: string
 * }
 *
 * @param {object} titleObj - { title: string, description?: string }
 * @param {string} model - model alias (e.g., "gpt-4o")
 * @param {boolean} backgroundDetails - if true, backend spawns detail job and returns detailJobId
 */
export async function generateScreenplayOutline(titleObj, model, backgroundDetails = true) {
  return postJSON('/api/text/generateScreenplayOutline', {
    title: titleObj,
    model,
    backgroundDetails,
  });
}

/**
 * NEW: Poll screenplay details job by id.
 * Returns one of:
 *   { status: "queued" | "running" }
 *   { status: "done", result: {...strict JSON detailed screenplay...} }
 *   { status: "error", error: string }
 *   404 -> throws
 */
export async function getScreenplayDetailJob(jobId) {
  const r = await getJSON(`/api/text/screenplayDetailJob/${encodeURIComponent(jobId)}`);
  return r;
}

/**
 * Existing: Enhanced character bible (kept as-is).
 * Historically returns { text }.
 */
export async function generateCharacterBibleEnhanced(screenPlay, model) {
  const { text } = await postJSON('/api/text/generateCharacterBibleEnhanced', { screenPlay, model });
  return text;
}

/**
 * Existing: Character bible (kept as-is).
 * Historically returns { text }.
 */
export async function generateCharacterBible(screenPlay, model = 'gpt-4.1', story) {
  const { text } = await postJSON('/api/text/generateCharacterBible', { screenPlay, model, story });
  return text;
}

/**
 * Existing: Concise character description (kept as-is).
 * Historically returns { text }.
 */
export async function generateConciseCharacterDescription(character, model = 'gpt-4.1') {
  const { text } = await postJSON('/api/text/generateConciseCharacterDescription', { character, model });
  return text;
}

export async function enrichCharacters({ projectId, characterMap, imageModel = 'gpt-image-1', textModel = 'gpt-4.1' }) {
  return postJSON('/api/text/enrichCharacters', { projectId, characterMap, imageModel, textModel });
}

export async function generateCharacterBibleStrict(payload) {
  const {
    titleObj = { title: 'Untitled', description: '' },
    model = 'gemini-3-pro-image-preview',
    outline = '',
    styleGuide = 'Realistic, IMAX-grade; grounded physics; Nolan-esque tone.',
  } = payload || {};

  return postJSON('/api/text/generateCharacterBibleJson', {
    model,
    title: titleObj,
    style_guide: styleGuide,
    outline
  });
}

/**
 * Regenerate a single character's full details (BIBLE format).
 */
export async function regenerateCharacterFull({ characterName, currentDetails, screenplayContext, storyContext, styleGuide, model, instructions }) {
  const { text } = await postJSON('/api/text/regenerateCharacterFull', {
    characterName,
    currentDetails,
    screenplayContext,
    storyContext,
    styleGuide,
    model,
    instructions
  });

  // Parse strict JSON from backend response if it's a string
  try {
    return typeof text === 'string' ? JSON.parse(text) : text;
  } catch (e) {
    console.warn("Failed to parse regenerated character JSON:", e);
    return null;
  }
}

/**
 * Validate text content against RAI guardrails.
 * Returns { allowed: boolean, reason: string|null }
 */
export async function validateContent(text, context = 'Screenplay content') {
  return postJSON('/api/safety/validate', { text, context });
}